import { apiClient } from '@/api/client';
import type {
  ItemStatus,
  List,
  ListItem,
  ListMember,
  PaginatedListItemResponse,
  PaginatedListResponse,
} from '@/features/lists/types';
import {
  mapListItemWireToDomain,
  mapListMemberWireToDomain,
  mapListWireToDomain,
} from '../mappers/list';
import {
  listItemStatusResponseSchema,
  listItemWireSchema,
  listMemberWireSchema,
  listWireSchema,
  paginatedListItemResponseSchema,
  paginatedListResponseSchema,
  type ListCreateInput,
  type ListItemCreateInput,
  type ListItemUpdateInput,
  type ListMemberCreateInput,
  type ListUpdateInput,
  type ListItemWire,
  type ListMemberWire,
  type ListWire,
  type PaginatedListItemResponseWire,
  type PaginatedListResponseWire,
  type ListItemStatusResponseWire,
} from '../schemas/list';

/**
 * Domain-oriented API module for list operations
 * Provides typed functions for list CRUD from the Gateway API
 */

// Request DTOs — the input shapes callers pass to the mutation endpoints below.
// Re-exported here so feature code imports them from the endpoint seam rather
// than reaching into wire schemas (see CONTEXT.md, data-layer seam).
export type {
  ListCreateInput,
  ListUpdateInput,
  ListItemCreateInput,
  ListItemUpdateInput,
  ListMemberCreateInput,
} from '../schemas/list';

// ============================
// Query Parameters
// ============================

export interface ListListsParams {
  page?: number;
  per_page?: number;
  sort_by?: 'name' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
  scope?: 'owned' | 'shared' | 'public';
  search?: string;
}

export interface ListItemsParams {
  page?: number;
  per_page?: number;
  sort_by?: 'created_at' | 'name' | 'business_name';
  sort_order?: 'asc' | 'desc';
}

// ============================
// Helpers
// ============================

/**
 * Build query string from a list of key-value pairs
 */
function buildQueryString(entries: Array<[string, string | number | undefined]>): string {
  const queryParts: string[] = [];

  for (const [key, value] of entries) {
    if (value !== undefined) {
      queryParts.push(`${key}=${encodeURIComponent(value)}`);
    }
  }

  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

/**
 * Map a paginated wire list response to domain types
 */
function mapPaginatedListResponse(wire: PaginatedListResponseWire): PaginatedListResponse {
  return {
    items: wire.items.map(mapListWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

/**
 * Map a paginated wire list item response to domain types
 */
function mapPaginatedListItemResponse(wire: PaginatedListItemResponseWire): PaginatedListItemResponse {
  return {
    items: wire.items.map(mapListItemWireToDomain),
    total: wire.total,
    page: wire.page,
    perPage: wire.per_page,
    totalPages: wire.total_pages,
  };
}

// ============================
// List CRUD
// ============================

/**
 * Create a new custom list
 *
 * POST /api/lists
 */
export async function createList(data: ListCreateInput): Promise<List> {
  const endpoint = '/api/lists';
  const wireData = await apiClient.post<ListWire>(endpoint, data);
  const validated = listWireSchema.parse(wireData);
  return mapListWireToDomain(validated);
}

/**
 * Get the current user's lists (paginated)
 *
 * GET /api/lists
 */
export async function getLists(params: ListListsParams = {}): Promise<PaginatedListResponse> {
  const queryParams: ListListsParams = {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildQueryString([
    ['page', queryParams.page],
    ['per_page', queryParams.per_page],
    ['sort_by', queryParams.sort_by],
    ['sort_order', queryParams.sort_order],
    ['scope', queryParams.scope],
    ['search', queryParams.search],
  ]);
  const endpoint = `/api/lists${queryString}`;
  const wireData = await apiClient.get<PaginatedListResponseWire>(endpoint);
  const validated = paginatedListResponseSchema.parse(wireData);
  return mapPaginatedListResponse(validated);
}

/**
 * Get a single list by ID
 *
 * GET /api/lists/:id
 */
export async function getList(id: string): Promise<List> {
  const endpoint = `/api/lists/${id}`;
  const wireData = await apiClient.get<ListWire>(endpoint);
  const validated = listWireSchema.parse(wireData);
  return mapListWireToDomain(validated);
}

/**
 * Update an existing list
 *
 * PUT /api/lists/:id
 */
export async function updateList(id: string, data: ListUpdateInput): Promise<List> {
  const endpoint = `/api/lists/${id}`;
  const wireData = await apiClient.put<ListWire>(endpoint, data);
  const validated = listWireSchema.parse(wireData);
  return mapListWireToDomain(validated);
}

/**
 * Delete a list (custom lists only)
 *
 * DELETE /api/lists/:id
 */
export async function deleteList(id: string): Promise<void> {
  const endpoint = `/api/lists/${id}`;
  await apiClient.delete(endpoint);
}

// ============================
// List Items
// ============================

/**
 * Add an item (menu_item or business) to a list
 *
 * POST /api/lists/:id/items
 */
export async function addListItem(listId: string, data: ListItemCreateInput): Promise<ListItem> {
  const endpoint = `/api/lists/${listId}/items`;
  const wireData = await apiClient.post<ListItemWire>(endpoint, data);
  const validated = listItemWireSchema.parse(wireData);
  return mapListItemWireToDomain(validated);
}

/**
 * Get paginated items in a list
 *
 * GET /api/lists/:id/items
 */
export async function getListItems(
  listId: string,
  params: ListItemsParams = {}
): Promise<PaginatedListItemResponse> {
  const queryParams: ListItemsParams = {
    page: 1,
    per_page: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
  };

  const queryString = buildQueryString([
    ['page', queryParams.page],
    ['per_page', queryParams.per_page],
    ['sort_by', queryParams.sort_by],
    ['sort_order', queryParams.sort_order],
  ]);
  const endpoint = `/api/lists/${listId}/items${queryString}`;
  const wireData = await apiClient.get<PaginatedListItemResponseWire>(endpoint);
  const validated = paginatedListItemResponseSchema.parse(wireData);
  return mapPaginatedListItemResponse(validated);
}

/**
 * Update the note on a list item
 *
 * PUT /api/lists/:id/items/:itemId
 */
export async function updateListItemNote(
  listId: string,
  itemId: string,
  data: ListItemUpdateInput
): Promise<ListItem> {
  const endpoint = `/api/lists/${listId}/items/${itemId}`;
  const wireData = await apiClient.put<ListItemWire>(endpoint, data);
  const validated = listItemWireSchema.parse(wireData);
  return mapListItemWireToDomain(validated);
}

/**
 * Remove an item from a list
 *
 * DELETE /api/lists/:id/items/:itemId?item_type=...
 */
export async function removeListItem(listId: string, itemId: string, itemType: string): Promise<void> {
  const endpoint = `/api/lists/${listId}/items/${itemId}?item_type=${encodeURIComponent(itemType)}`;
  await apiClient.delete(endpoint);
}

/**
 * Check which of the user's lists contain a given item
 *
 * GET /api/lists/item-status?item_type=...&item_id=...
 */
export async function getItemStatus(itemType: string, itemId: string): Promise<ItemStatus> {
  const queryString = buildQueryString([
    ['item_type', itemType],
    ['item_id', itemId],
  ]);
  const endpoint = `/api/lists/item-status${queryString}`;
  const wireData = await apiClient.get<ListItemStatusResponseWire>(endpoint);
  const validated = listItemStatusResponseSchema.parse(wireData);
  return { listIds: validated.list_ids };
}

// ============================
// List Members
// ============================

/**
 * Invite a user to a list as a member
 *
 * POST /api/lists/:id/members
 */
export async function inviteListMember(
  listId: string,
  data: ListMemberCreateInput
): Promise<ListMember> {
  const endpoint = `/api/lists/${listId}/members`;
  const wireData = await apiClient.post<ListMemberWire>(endpoint, data);
  const validated = listMemberWireSchema.parse(wireData);
  return mapListMemberWireToDomain(validated);
}

/**
 * Get members of a list
 *
 * GET /api/lists/:id/members
 */
export async function getListMembers(listId: string): Promise<ListMember[]> {
  const endpoint = `/api/lists/${listId}/members`;
  const response = await apiClient.get<{ members: ListMemberWire[] }>(endpoint);
  const validated = response.members.map((item) => listMemberWireSchema.parse(item));
  return validated.map(mapListMemberWireToDomain);
}

/**
 * Accept a list invitation (update member status to accepted)
 *
 * PUT /api/lists/:id/members/:memberId
 */
export async function acceptListInvitation(
  listId: string,
  memberId: string
): Promise<ListMember> {
  const endpoint = `/api/lists/${listId}/members/${memberId}`;
  const wireData = await apiClient.put<ListMemberWire>(endpoint, { status: 'accepted' });
  const validated = listMemberWireSchema.parse(wireData);
  return mapListMemberWireToDomain(validated);
}

/**
 * Reject a list invitation by removing the member record
 *
 * DELETE /api/lists/:id/members/:memberId
 */
export async function rejectListInvitation(
  listId: string,
  memberId: string
): Promise<void> {
  const endpoint = `/api/lists/${listId}/members/${memberId}`;
  await apiClient.delete(endpoint);
}

/**
 * Respond to a list invitation (accept or decline)
 * Resolves the pending membership server-side from the JWT user.
 *
 * POST /api/lists/:id/respond-invitation
 */
export async function respondToListInvitation(
  listId: string,
  action: 'accept' | 'decline'
): Promise<ListMember | void> {
  const endpoint = `/api/lists/${listId}/respond-invitation`;
  if (action === 'accept') {
    const wireData = await apiClient.post<ListMemberWire>(endpoint, { action });
    const validated = listMemberWireSchema.parse(wireData);
    return mapListMemberWireToDomain(validated);
  }
  await apiClient.post(endpoint, { action });
}

/**
 * Remove a member from a list (owner action) or leave a list (self-removal)
 *
 * DELETE /api/lists/:id/members/:memberId
 */
export async function removeListMember(
  listId: string,
  memberId: string
): Promise<void> {
  const endpoint = `/api/lists/${listId}/members/${memberId}`;
  await apiClient.delete(endpoint);
}
